import React, { useState, useEffect, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { baseUrl, assetFullLabel } from '../../shared';
import { StatusChip } from '../ui';
import TakeOfflineDialog from '../pwa/TakeOfflineDialog';
import { isVisitOffline } from '../../offline/takeOffline';

dayjs.extend(isoWeek);

const authHeaders = () => ({
  'Content-Type': 'application/json',
  username: localStorage.getItem('username'),
  bToken: localStorage.getItem('bToken'),
});

const STATUS_LABELS = {
  planned: 'Planned',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

function Section({ title, children }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700, mb: 0.5, textTransform: 'uppercase', fontSize: 11, letterSpacing: 1 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <Box sx={{ mb: 0.5 }}>
      <Typography variant="body2" component="span" sx={{ fontWeight: 600, mr: 1 }}>{label}:</Typography>
      <Typography variant="body2" component="span">{value}</Typography>
    </Box>
  );
}

export default function VisitDetailModal({ open, onClose, visit, onEdit, onReport, onDelete, onArchive, onUnarchive, onInspect, onStatusChange }) {
  const [statusAnchor, setStatusAnchor] = useState(null);
  const [patching, setPatching] = useState(false);
  const [takeOfflineOpen, setTakeOfflineOpen] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);

  const visitId = visit && visit.id;
  const refreshOfflineState = useCallback(() => {
    if (visitId == null) { setOfflineReady(false); return; }
    isVisitOffline(visitId).then(setOfflineReady);
  }, [visitId]);
  useEffect(() => { if (open) refreshOfflineState(); }, [open, refreshOfflineState]);

  if (!visit) return null;
  const isAdmin = localStorage.getItem('canManageSchedule') === '1';
  const isStaff = localStorage.getItem('canViewSchedule') === '1';
  const canInspect = localStorage.getItem('canUseDetailedInspection') === '1' || localStorage.getItem('canInspectAsset') === '1';
  const canChangeStatus = isAdmin || isStaff;

  const startDate = dayjs(visit.start_date);
  const endDate = dayjs(visit.end_date);
  const weekNum = startDate.isoWeek();

  const handleStatusPatch = async (status) => {
    setStatusAnchor(null);
    if (status === visit.status) return;
    setPatching(true);
    try {
      const res = await fetch(baseUrl + 'schedule/visits/' + visit.id, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ status }),
      });
      if (res.ok && onStatusChange) onStatusChange(status);
    } finally {
      setPatching(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { mb: '6vh', maxHeight: 'calc(100vh - 8vh)' } }}>
      <DialogTitle>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
            <Typography variant="h6">{visit.company}</Typography>
            <StatusChip
              domain="visit"
              value={visit.status}
              label={STATUS_LABELS[visit.status] || visit.status}
              size="md"
              onClick={canChangeStatus ? (e) => setStatusAnchor(e.currentTarget) : undefined}
              style={{ cursor: canChangeStatus ? 'pointer' : 'default' }}
            />
            {patching && <CircularProgress size={16} />}
            {isAdmin && (
              <Tooltip title="Edit visit">
                <IconButton onClick={onEdit} color="primary" size="small"><EditIcon fontSize="small" /></IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Status picker — quick-change from the chip (mirrors workshop jobs) */}
          <Menu anchorEl={statusAnchor} open={!!statusAnchor} onClose={() => setStatusAnchor(null)}>
            {Object.entries(STATUS_LABELS).map(([val, label]) => (
              <MenuItem
                key={val}
                selected={visit.status === val}
                onClick={() => handleStatusPatch(val)}
                sx={{ fontWeight: visit.status === val ? 700 : 400 }}
              >
                {label}
              </MenuItem>
            ))}
          </Menu>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
            {(visit.sites || []).map(s => (
              <Chip key={s} label={s} size="small" variant="outlined" />
            ))}
            {(!visit.sites || visit.sites.length === 0) && visit.site_locations && (
              <Typography variant="body2" color="text.secondary">{visit.site_locations}</Typography>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {startDate.format('DD/MM/YYYY')} – {endDate.format('DD/MM/YYYY')} &nbsp;|&nbsp; Week {weekNum}
          </Typography>
          {visit.archivedAt && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
              Archived {dayjs(visit.archivedAt).format('DD/MM/YYYY HH:mm')}{visit.archived_by_name ? ` by ${visit.archived_by_name}` : ''}
            </Typography>
          )}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Section title="Work Details">
          <Field label="Work Type" value={visit.work_type_name} />
          {visit.sow && (
            <Box sx={{ mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Scope of Work:</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', pl: 1 }}>{visit.sow}</Typography>
            </Box>
          )}
          <Field label="PO #" value={visit.po_number} />
          <Field label="Work Order #" value={visit.work_order_number} />
          <Field label="Job Card #" value={visit.job_card_number} />
          <Field label="Quoted Labour Hours" value={visit.quoted_labour_hours} />
        </Section>

        {visit.staff && visit.staff.length > 0 && (
          <Section title="Assigned Staff">
            {visit.staff.map(s => (
              <Typography key={s.user_id} variant="body2">• {s.name}{s.email ? ` (${s.email})` : ''}</Typography>
            ))}
          </Section>
        )}

        {visit.logistics && visit.logistics.length > 0 && (
          <Section title="Logistics">
            {visit.logistics.map(l => (
              <Typography key={l.logistics_type_id} variant="body2">
                • {l.name}{l.details ? `: ${l.details}` : ''}
              </Typography>
            ))}
          </Section>
        )}

        {visit.assets && visit.assets.length > 0 && (
          <Section title={`Assets (${visit.assets.length})`}>
            {visit.assets.map(a => (
              <Box key={a.cat_no} sx={{ mb: 0.5 }}>
                <Typography variant="body2">
                  <strong>{assetFullLabel(a)}</strong>{a.item ? ` — ${a.item}` : ''}{a.type ? ` [${a.type}]` : ''}
                </Typography>
                {(a.manufacturer || a.model) && (
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                    {[a.manufacturer, a.model].filter(Boolean).join(' ')}
                    {a.lastInspectionDate ? ` | Last insp: ${a.lastInspectionDate}` : ''}
                    {a.inspectionStatus ? ` | ${a.inspectionStatus}` : ''}
                  </Typography>
                )}
              </Box>
            ))}
          </Section>
        )}

        {visit.comments && (
          <Section title="Comments">
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{visit.comments}</Typography>
          </Section>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', borderTop: '1px solid var(--border)' }}>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {isAdmin && onDelete && (
            <Tooltip title="Delete visit">
              <IconButton onClick={() => onDelete(visit.id)} color="error" size="small"><DeleteIcon fontSize="small" /></IconButton>
            </Tooltip>
          )}
          {isAdmin && !visit.archivedAt && onArchive && (
            <Tooltip title="Archive visit">
              <IconButton onClick={() => onArchive(visit.id)} size="small"><ArchiveIcon fontSize="small" /></IconButton>
            </Tooltip>
          )}
          {isAdmin && visit.archivedAt && onUnarchive && (
            <Tooltip title="Restore from archive">
              <IconButton onClick={() => onUnarchive(visit.id)} size="small"><UnarchiveIcon fontSize="small" /></IconButton>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {canInspect && visit.assets && visit.assets.length > 0 && (
            <Button
              onClick={() => setTakeOfflineOpen(true)}
              variant="outlined" size="small"
              color={offlineReady ? 'success' : 'primary'}
              startIcon={offlineReady ? <CloudDoneIcon /> : <CloudOffIcon />}
            >
              {offlineReady ? 'Offline ready' : 'Take offline'}
            </Button>
          )}
          {canInspect && onInspect && visit.assets && visit.assets.length > 0 && (
            <Button onClick={() => onInspect(visit)} variant="outlined" size="small">Site Inspection</Button>
          )}
          {(isAdmin || isStaff) && (
            <Button onClick={onReport} variant="outlined" size="small">Download Report</Button>
          )}
          {!isAdmin && isStaff && (
            <Button onClick={onEdit} variant="contained" size="small">Update Status</Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </Box>
      </DialogActions>

      <TakeOfflineDialog
        open={takeOfflineOpen}
        onClose={() => setTakeOfflineOpen(false)}
        visit={visit}
        onDone={refreshOfflineState}
      />
    </Dialog>
  );
}
